import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import mascot from "@/assets/mascot.png";
import { useLang } from "@/hooks/use-language";
import {
  listPublicParentArticles,
  type ParentArticleListItem,
} from "@/lib/parents.functions";
import { BookOpen, Clock } from "lucide-react";

export const Route = createFileRoute("/parents")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/parents" },
      { title: "Parents Hub | للأهل — جذوري" },
      {
        name: "description",
        content:
          "مقالات ونصائح للأهل من جذوري حول التربية الإسلامية، الحوار مع الأطفال، التعليم ثنائي اللغة، والقيم. Parenting guides for raising bilingual, confident Muslim kids.",
      },
      { property: "og:title", content: "Parents Hub — Jozori | جذوري" },
      {
        property: "og:description",
        content:
          "Practical, research-backed parenting articles for raising bilingual, confident, value-driven kids.",
      },
      { property: "og:image", content: "https://jozori.com/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://jozori.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/parents" }],
  }),
  loader: () => listPublicParentArticles(),
  component: ParentsRouteShell,
});

function ParentsRouteShell() {
  const { pathname } = useLocation();
  return pathname === "/parents" ? <ParentsIndex /> : <Outlet />;
}

function ParentsIndex() {
  const articles = Route.useLoaderData() as ParentArticleListItem[];
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";

  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-clay/30 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display-en text-lg font-black">
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span>Jozori</span>
          </Link>
          <button
            onClick={toggle}
            className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
          >
            {isAr ? "English" : "العربية"}
          </button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-secondary/15 via-accent/10 to-primary/15"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-secondary">
            {isAr ? "ركن الأهل" : "Parents Hub"}
          </p>
          <h1
            className={`mx-auto max-w-3xl text-4xl md:text-5xl font-black leading-tight ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {isAr
              ? "أدلّة عملية لتربية أطفال بثقة وقيم"
              : "Practical guides for raising confident, values-driven kids"}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-foreground/70">
            {isAr
              ? "مقالات قصيرة مبنية على البحث، يكتبها خبراء التربية، تساعدك على فتح حوارات معنى مع طفلك حول الصدق، الصبر، الهوية، والإيمان."
              : "Short, research-backed articles by parenting experts — to help you open meaningful conversations with your child about honesty, patience, identity and faith."}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        {articles.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="size-10 mx-auto mb-3 text-foreground/40" />
            <p className="text-foreground/60">
              {isAr ? "قريبًا — مقالات جديدة كل أسبوع." : "Coming soon — new articles weekly."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((a) => (
              <Link
                key={a.id}
                to="/parents/$slug"
                params={{ slug: a.slug }}
                className="group rounded-3xl border-2 border-clay/30 bg-card overflow-hidden hover:border-secondary hover:-translate-y-1 transition-all"
              >
                {a.cover_url ? (
                  <div className="aspect-[16/9] overflow-hidden bg-clay/20">
                    <img
                      src={a.cover_url}
                      alt=""
                      className="size-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-secondary/30 to-primary/20 flex items-center justify-center text-5xl">
                    👨‍👩‍👧
                  </div>
                )}
                <div className="p-5">
                  <h2
                    className={`text-lg font-black leading-snug line-clamp-2 ${
                      isAr ? "font-display-ar" : "font-display-en"
                    }`}
                  >
                    {isAr ? a.title_ar : a.title_en}
                  </h2>
                  {(isAr ? a.description_ar : a.description_en) && (
                    <p className="mt-2 text-sm text-foreground/70 line-clamp-3">
                      {isAr ? a.description_ar : a.description_en}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-3 text-[11px] font-bold text-foreground/50">
                    {a.reading_minutes ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="size-3" />
                        {a.reading_minutes} {isAr ? "د قراءة" : "min read"}
                      </span>
                    ) : null}
                    <span className="text-secondary">{isAr ? "اقرأ ←" : "Read →"}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
